import { useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { useAuthStore } from '../store/auth';
import { getApiClient } from '../api/factory';
import { updateLocale } from '../api/auth';
import { useLocale, useT, type Locale } from '../i18n';
import { PushSettings } from '../components/PushSettings';
import { NotificationSettings } from '../components/NotificationSettings';
import { DesktopSettings } from '../components/DesktopSettings';
import { PasskeysSection } from '../components/PasskeysSection';
import { TenantSecurityPolicySection } from '../components/TenantSecurityPolicySection';
import { ScheduledReportsSection } from '../components/ScheduledReportsSection';
import { VisitorBansSection } from '../components/VisitorBansSection';
import { ConversationTagsAdmin } from '../components/ConversationTagsAdmin';
import { DepartmentsAdmin } from '../components/DepartmentsAdmin';
import { PageContainer, PageHeader } from '../components/Page';

// Минимальная страница профиля оператора. На MVP — read-only из локальной
// сессии; смена email/имени/пароля придёт отдельным флоу (нужен /api/v1/me
// PATCH с подтверждением старого пароля).
export function SettingsPage() {
  const operator = useAuthStore((s) => s.session?.operator);
  const setSession = useAuthStore((s) => s.setSession);
  const [emailCopied, setEmailCopied] = useState(false);
  const t = useT();
  const [locale, setLocale] = useLocale();

  // ANX-036: ручная смена языка применяется мгновенно (setLocale → localStorage)
  // и параллельно сохраняется на backend, чтобы preference переехал на другие
  // устройства оператора. После успеха синхронизируем session.operator.locale,
  // иначе useServerLocaleSync при следующем рендере не должен ничего отыгрывать.
  const localeMu = useMutation({
    mutationFn: (l: Locale) => updateLocale(getApiClient(), l),
    onSuccess: (op) => {
      const s = useAuthStore.getState().session;
      if (s) setSession({ ...s, operator: { ...s.operator, locale: op.locale } });
    },
  });

  function changeLocale(l: Locale) {
    if (l === locale) return;
    setLocale(l);
    localeMu.mutate(l);
  }

  if (!operator) {
    return (
      <PageContainer width="narrow">
        <p className="text-sm text-slate-500">{t('common.notLoaded')}</p>
      </PageContainer>
    );
  }

  async function copy(text: string, setter: (v: boolean) => void) {
    try {
      await navigator.clipboard.writeText(text);
      setter(true);
      setTimeout(() => setter(false), 1500);
    } catch {
      // ignore
    }
  }

  return (
    <PageContainer width="narrow">
      <PageHeader title={t('settings.title')} />

      <dl className="divide-y divide-slate-200 rounded-md border border-slate-200 bg-white shadow-card">
        <Field label={t('settings.name')} value={operator.name} />
        <Field
          label={t('settings.email')}
          value={operator.email}
          actionLabel={emailCopied ? t('common.copied') : t('common.copy')}
          onAction={() => copy(operator.email, setEmailCopied)}
        />
        <Field label={t('settings.role')} value={operator.role} />
        <Field label={t('settings.operatorId')} value={operator.id} mono />
        <Field label={t('settings.tenantId')} value={operator.tenantId} mono />
      </dl>

      <div className="mt-4 rounded-md border border-slate-200 bg-white p-4 shadow-card">
        <label className="mb-2 block text-xs text-slate-500">{t('settings.language')}</label>
        <select
          value={locale}
          onChange={(e) => changeLocale(e.target.value as Locale)}
          className="rounded-md border border-slate-200 bg-white px-2 py-1.5 text-sm focus:border-accent-400 focus:outline-none focus:ring-2 focus:ring-accent-100"
        >
          <option value="ru">{t('settings.language.ru')}</option>
          <option value="en">{t('settings.language.en')}</option>
        </select>
      </div>

      <PushSettings />

      <NotificationSettings />

      <DesktopSettings />

      <PasskeysSection />

      <TenantSecurityPolicySection />

      <ScheduledReportsSection />

      <VisitorBansSection />

      <ConversationTagsAdmin />

      <DepartmentsAdmin />

      <p className="mt-4 text-xs text-slate-500">{t('settings.passwordHint')}</p>
    </PageContainer>
  );
}

function Field({
  label,
  value,
  mono,
  actionLabel,
  onAction,
}: {
  label: string;
  value: string;
  mono?: boolean;
  actionLabel?: string;
  onAction?: () => void;
}) {
  return (
    <div className="flex items-center justify-between px-4 py-3">
      <div className="w-32 text-xs text-slate-500">{label}</div>
      <div
        className={
          'flex-1 text-sm ' + (mono ? 'font-mono text-slate-600' : 'text-slate-900')
        }
      >
        {value}
      </div>
      {actionLabel && (
        <button
          type="button"
          onClick={onAction}
          className="text-xs text-accent-600 hover:text-accent-700"
        >
          {actionLabel}
        </button>
      )}
    </div>
  );
}
